import { Database, FilePlus2, FolderOpen, Download, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supportsFileSystemAccess } from "@/lib/fs";

export default function DbOnboarding({
  open,
  onOpenChange,
  onCreateNew,
  onOpenExisting,
  onImportLegacy,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="db-onboarding"
        className="sm:max-w-[560px] rounded-none border-2 border-[#0A0A0A] p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-5 border-b-2 border-[#0A0A0A] bg-[#FAFAFA]">
          <DialogTitle className="text-xl font-extrabold tracking-tighter flex items-center gap-2">
            <Database size={20} strokeWidth={2.5} />
            Connect Your Database
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            All your data — company details, parties, invoices — lives in a
            single <span className="font-mono">.db</span> file on your device.
            Pick where to keep it. Nothing is sent to any server.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-3">
          {!supportsFileSystemAccess && (
            <div
              data-testid="db-onboarding-unsupported"
              className="border border-[#FFA500] bg-[#FFF8E1] px-3 py-2 text-[11px] text-[#0A0A0A] leading-relaxed"
            >
              <span className="font-bold uppercase tracking-widest">
                Heads up:
              </span>{" "}
              your browser doesn't support folder-based file access. You'll be
              able to <span className="font-bold">Save</span> a downloaded
              database file and <span className="font-bold">Open</span> it next
              time. Use Chrome / Edge / Brave on desktop for the seamless
              experience.
            </div>
          )}

          <button
            type="button"
            onClick={onCreateNew}
            data-testid="db-create-new-btn"
            className="w-full text-left flex items-center gap-3 border-2 border-[#0A0A0A] p-4 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors group"
          >
            <FilePlus2 size={20} strokeWidth={2.5} className="shrink-0" />
            <div className="flex-1">
              <div className="font-extrabold uppercase tracking-widest text-xs">
                Create New Database
              </div>
              <div className="text-[11px] mt-0.5 opacity-70">
                {supportsFileSystemAccess
                  ? "Pick a folder and a name. The file will be saved there."
                  : "Start fresh. You'll save the .db file when you click Save."}
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={onOpenExisting}
            data-testid="db-open-existing-btn"
            className="w-full text-left flex items-center gap-3 border-2 border-[#0A0A0A] p-4 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            {supportsFileSystemAccess ? (
              <FolderOpen size={20} strokeWidth={2.5} className="shrink-0" />
            ) : (
              <Upload size={20} strokeWidth={2.5} className="shrink-0" />
            )}
            <div className="flex-1">
              <div className="font-extrabold uppercase tracking-widest text-xs">
                Open Existing Database
              </div>
              <div className="text-[11px] mt-0.5 opacity-70">
                Continue from a previously saved{" "}
                <span className="font-mono">.db</span> file.
              </div>
            </div>
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="h-px bg-[#E5E5E5] flex-1" />
            <span className="text-[9px] uppercase tracking-[0.25em] text-[#A1A1AA] font-bold">
              Or
            </span>
            <div className="h-px bg-[#E5E5E5] flex-1" />
          </div>

          <button
            type="button"
            onClick={onImportLegacy}
            data-testid="db-skip-btn"
            className="w-full text-[10px] uppercase tracking-widest font-bold text-[#52525B] hover:text-[#0A0A0A] py-2"
          >
            Continue without choosing a file (data will be lost on refresh)
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
